import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import ReservationForm, { ReservationFormData } from '../ReservationForm';

export { }; // Make this a module

interface Reservation {
  id: string;
  clientId?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  startDate: string;
  endDate: string;
  status: string;
  rescheduleDate?: string;
  paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
  notes?: string;
  board?: {
    id: string;
    name: string;
    type: string;
  };
}

interface ReservationEditDialogProps {
  open: boolean;
  reservation: Reservation | null;
  businessId: string;
  board?: {
    id: string;
    name: string;
    type: string;
  } | null;
  onClose: () => void;
  onSave: (reservation: Partial<Reservation>) => void;
}

const ReservationEditDialog: React.FC<ReservationEditDialogProps> = ({
  open,
  reservation,
  businessId,
  board,
  onClose,
  onSave,
}) => {

  const isCreateMode = !reservation;

  const initialData = useMemo(() => {
    if (isCreateMode) {
      // For create mode, provide default values
      const now = new Date();
      const start = new Date(now);
      start.setHours(20, 0, 0, 0); // Default to 8 PM
      const end = new Date(start);
      end.setHours(21, 0, 0, 0); // Default to 9 PM

      return {
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        boardId: board?.id || '',
        startDate: start,
        endDate: start, // Same day
        startTime: start,
        endTime: end,
        notes: '',
        status: 'confirmed' as any,
        paymentStatus: 'pending' as any,
        rescheduleDate: null
      };
    }

    // Edit mode
    const start = new Date(reservation.startDate);
    const end = new Date(reservation.endDate);

    // Use board prop if provided, otherwise fall back to reservation.board
    const boardId = board?.id || reservation.board?.id || '';

    return {
      customerName: reservation.customerName,
      customerEmail: reservation.customerEmail,
      customerPhone: reservation.customerPhone || '',
      boardId: boardId,
      startDate: start,
      endDate: end,
      startTime: start,
      endTime: end,
      notes: reservation.notes || '',
      status: reservation.status as any,
      paymentStatus: reservation.paymentStatus || 'pending',
      rescheduleDate: reservation.rescheduleDate ? new Date(reservation.rescheduleDate) : null
    };
  }, [reservation, board, isCreateMode]);

  const handleSubmit = async (data: ReservationFormData) => {
    // Convert back to Partial<Reservation>
    const start = new Date(data.startDate);
    start.setHours(data.startTime.getHours(), data.startTime.getMinutes());

    const end = new Date(data.endDate);
    end.setHours(data.endTime.getHours(), data.endTime.getMinutes());

    const updates: Partial<Reservation> = {
      customerName: data.customerName,
      customerEmail: data.customerEmail,
      customerPhone: data.customerPhone,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      status: data.status,
      paymentStatus: data.paymentStatus,
      notes: data.notes,
    };

    if (data.status === 'cancelled' && data.rescheduleDate) {
      updates.rescheduleDate = data.rescheduleDate.toISOString();
    }

    onSave(updates);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreateMode ? 'Nueva Reserva' : 'Editar Reserva'}</DialogTitle>
      <DialogContent>
        <ReservationForm
          initialData={initialData}
          onSubmit={async (data) => handleSubmit(data)}
          onCancel={onClose}
          isEditMode={!isCreateMode}
          boards={board ? [board] : (reservation?.board ? [reservation.board] : [])}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ReservationEditDialog;